import { Modal } from 'antd'
import { useCallback } from 'react'

import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'

import { sanitizeReceiptHtml } from './receipt-html'

import styles from './receipt-view-dialog.module.scss'

// Placed before the receipt so its own :host rules (converted from body) still win.
const HOST_BASE_STYLE = '<style>:host{display:block}</style>'
// The payload centres itself at max-width:800px; let it use the dialog we give it.
const FILL_WIDTH_STYLE = '<style>.receipt-container{max-width:100%!important}</style>'

const STYLE_BLOCK = /(<style[^>]*>)([\s\S]*?)(<\/style>)/gi
const ROOT_SELECTOR = /(^|[{},;]|\s)(html|body)(?=[\s,{:.#[>]|$)/gi

// A shadow root has no <html>/<body>, so the payload's page-level rules — background,
// base font, colours — would match nothing. Retarget them at the host element.
const scopeRootSelectors = (html: string): string =>
  html.replace(STYLE_BLOCK, (_block, open: string, css: string, close: string) =>
    open.concat(css.replace(ROOT_SELECTOR, '$1:host'), close),
  )

type Props = {
  open: boolean
  receiptHtml: string
  onClose: () => void
  onPrint: () => void
  onDownload: () => void
  isDownloading: boolean
  downloadError: string | null
}

export const ReceiptViewDialog = ({
  open,
  receiptHtml,
  onClose,
  onPrint,
  onDownload,
  isDownloading,
  downloadError,
}: Props) => {
  // Callback ref, not an effect: antd mounts the modal panel a tick after `open` flips.
  const mountReceipt = useCallback(
    (host: HTMLDivElement | null) => {
      if (!host) return
      // Shadow root scopes the receipt's generic selectors (.info-row, table) to the modal,
      // while keeping the page viewport so its own responsive rules match the source platform.
      const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
      const clean = sanitizeReceiptHtml(receiptHtml)
      root.innerHTML = HOST_BASE_STYLE + scopeRootSelectors(clean) + FILL_WIDTH_STYLE
    },
    [receiptHtml],
  )

  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      onCancel={onClose}
      destroyOnHidden
      centered
      width={1000}
      className={styles.modal}
      classNames={{ content: styles.modalContent }}>
      <div className={styles.toolbar}>
        <Button
          proportion={BUTTON_PROPORTION.LARGE}
          onClick={onPrint}
          className={`${styles.toolbarButton} ${styles.printButton}`}>
          Print
        </Button>
        <Button
          modifier={BUTTON_MODIFIER.SECONDARY}
          proportion={BUTTON_PROPORTION.LARGE}
          loading={isDownloading}
          onClick={onDownload}
          className={styles.toolbarButton}>
          Download
        </Button>
      </div>
      <div ref={mountReceipt} data-testid='payment-receipt' className={styles.receiptHost} />
      {downloadError && <p className={styles.printError}>{downloadError}</p>}
      <div className={styles.footer}>
        <Button
          modifier={BUTTON_MODIFIER.SECONDARY}
          proportion={BUTTON_PROPORTION.LARGE}
          onClick={onClose}
          className={styles.closeButton}>
          Close
        </Button>
      </div>
    </Modal>
  )
}
